import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  CircularProgress,
  DialogActions,
  TextField,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RepertoireData, RepertoireReducer, UserRepertoireData } from 'Types/RepertoireTypes';
import { Menu, useMenuIsDrawer } from 'Components/RoadmapComponents/Menu';
import PageNav from './PageNav';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import axios from 'axios';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import * as repertoireActions from '../../Actions/repertoire';
import { v5 as uuidv5 } from 'uuid';
import { isActiveProcessingStatus, isUserRepertoirePlayable } from './repertoireUploadState';
import { extractComposerFromMusicXml } from './musicXmlMetadata';
import PresignedImg from 'Components/PresignedImg';

const CREATED_REPERTOIRE_SOURCE = 'created';
const CREATED_REPERTOIRE_AUTHOR = 'you';
const MAX_UPLOAD_BYTES = 20 * 1024 * 1024;

// Idempotency key derived from the uploaded file's CONTENT + the OWNER, so
// re-uploading the same file returns the existing row instead of creating a
// duplicate (Song Import Phase 1, AC4). UUIDv5 keeps the key RFC-4122 valid for the
// backend's @IsUUID() check and is deterministic per (owner, file). Owner-namespacing
// is load-bearing: the backend uses this key AS the globally-unique row uuid, so an
// owner-independent key would collide when two different users upload the same file
// (500 + a cross-user existence oracle — a Decision #157 privacy break).
const REPERTOIRE_UPLOAD_NAMESPACE = '9e1f6b7c-3a2d-4e5f-8b0c-1d2e3f4a5b6c';
const deriveUploadIdempotencyKey = async (file: File, ownerSub: string): Promise<string> => {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const perOwnerNamespace = uuidv5(ownerSub, REPERTOIRE_UPLOAD_NAMESPACE);
  return uuidv5(bytes, perOwnerNamespace);
};

const upsertById = <T extends { id: number }>(items: T[], nextItem: T) => [
  nextItem,
  ...items.filter((item) => item.id !== nextItem.id),
];

const Library = () => {
  const dispatch = useDispatch();
  const { currentAuthToken } = useAuthWrapperContext();
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const userRepertoireData = repertoireReducerData?.userRepertoireData || [];
  const createdRepertoireData = userRepertoireData.filter(
    (rep) => rep.source === CREATED_REPERTOIRE_SOURCE,
  );
  const uploadedRepertoireData = userRepertoireData.filter(
    (rep) => rep.source !== CREATED_REPERTOIRE_SOURCE,
  );
  const hasUserRepertoire = userRepertoireData.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [_uploading, _setUploading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // On non-desktop devices (iPad, phones) the sidebar becomes a slidable drawer,
  // so the content fills the full width and the nav shows the menu toggle button.
  const isDrawer = useMenuIsDrawer(true);

  useEffect(() => {
    // Fetch user repertoire on mount
    fetchUserRepertoire();
  }, []);

  useEffect(() => {
    // Check for processing repertoire and poll for updates
    if (!userRepertoireData || !Array.isArray(userRepertoireData)) {
      return;
    }

    const processingItems = userRepertoireData.filter((rep) =>
      isActiveProcessingStatus(rep.status),
    );

    if (processingItems.length > 0) {
      const interval = setInterval(() => {
        checkProcessingStatus(processingItems.map((item) => item.id));
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [userRepertoireData]);

  const fetchUserRepertoire = async () => {
    try {
      const token = await currentAuthToken();
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      dispatch(repertoireActions.fetchUserRepertoireSuccess(response.data));
    } catch (error) {
      console.error('Error fetching user repertoire:', error);
    }
  };

  const checkProcessingStatus = async (ids: number[]) => {
    try {
      const token = await currentAuthToken();
      const results = await Promise.allSettled(
        ids.map(async (id) => {
          const response = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
            },
          );
          if (!isActiveProcessingStatus(response.data.status)) {
            dispatch(
              repertoireActions.setUserRepertoireProcessing({
                id,
                status: response.data.status,
              }),
            );
          }
          return response.data.status;
        }),
      );
      if (
        results.some(
          (result) => result.status === 'fulfilled' && !isActiveProcessingStatus(result.value),
        )
      ) {
        await fetchUserRepertoire();
      }
    } catch (error) {
      console.error('Error checking processing status:', error);
    }
  };

  const handleOpenUploadModal = () => {
    setUploadModalOpen(true);
  };

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100vh',
        width: '100vw',
      }}
    >
      <Menu
        selected={'Repertoire'}
        collapseToDrawer
        mobileOpen={menuOpen}
        setMobileOpen={setMenuOpen}
        hideMobileTrigger
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: isDrawer ? '100%' : 'calc(100% - var(--app-sidebar-width, 100px))',
        }}
      >
        <PageNav selected={'Library'} onMenuOpen={isDrawer ? () => setMenuOpen(true) : undefined} />
        <Box
          sx={{
            margin: '0px 0px',
            justifyContent: 'flex-start',
            height: 'calc(100vh - var(--rep-nav-height, 100px))',
            overflowY: 'auto',
            position: 'relative',
          }}
          ref={containerRef}
        >
          {!hasUserRepertoire ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Button
                data-repertoire-library-upload
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenUploadModal}
                sx={{
                  backgroundColor: '#469F6A',
                  color: 'white',
                  textTransform: 'uppercase',
                  fontWeight: '700',
                  fontSize: '15px',
                  padding: '12px 32px',
                  '&:hover': {
                    backgroundColor: '#3a8556',
                  },
                }}
              >
                Upload Repertoire
              </Button>
            </Box>
          ) : (
            <>
              {uploadedRepertoireData.length > 0 && (
                <RepertoireCardGrid topPadding={28}>
                  {uploadedRepertoireData.map((rep) => (
                    <UserRepertoireItem
                      key={rep.id}
                      repertoireData={rep}
                      onDelete={fetchUserRepertoire}
                    />
                  ))}
                </RepertoireCardGrid>
              )}
              {createdRepertoireData.length > 0 && (
                <>
                  <Box
                    sx={{
                      padding: uploadedRepertoireData.length > 0 ? '4px 28px 0' : '28px 28px 0',
                    }}
                  >
                    <Box sx={createdRepertoireDividerStyles}>
                      <Box sx={createdRepertoireDividerLineStyles} />
                      <Box sx={createdRepertoireDividerLabelStyles}>Your Created Repertoire</Box>
                      <Box sx={createdRepertoireDividerLineStyles} />
                    </Box>
                  </Box>
                  <RepertoireCardGrid topPadding={18}>
                    {createdRepertoireData.map((rep) => (
                      <UserRepertoireItem
                        key={rep.id}
                        repertoireData={rep}
                        onDelete={fetchUserRepertoire}
                      />
                    ))}
                  </RepertoireCardGrid>
                </>
              )}
              <IconButton
                data-repertoire-library-upload
                aria-label="Upload repertoire"
                onClick={handleOpenUploadModal}
                sx={{
                  position: 'fixed',
                  bottom: '32px',
                  right: '32px',
                  backgroundColor: '#469F6A',
                  color: 'white',
                  width: '56px',
                  height: '56px',
                  '&:hover': {
                    backgroundColor: '#3a8556',
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </>
          )}
        </Box>
      </Box>
      <UploadModal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        onSuccess={fetchUserRepertoire}
      />
    </Box>
  );
};

export default Library;

const RepertoireCardGrid = ({
  children,
  topPadding,
}: {
  children: React.ReactNode;
  topPadding: number;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))',
      gap: '20px',
      padding: `${topPadding}px 28px 28px`,
    }}
  >
    {children}
  </Box>
);

const createdRepertoireDividerStyles = {
  display: 'grid',
  gridTemplateColumns: 'minmax(24px, 1fr) auto minmax(24px, 1fr)',
  alignItems: 'center',
  gap: '14px',
};

const createdRepertoireDividerLineStyles = {
  height: '1px',
  backgroundColor: 'rgba(173, 177, 183, 0.28)',
};

const createdRepertoireDividerLabelStyles = {
  color: '#ADB1B7',
  fontFamily: 'Lato',
  fontSize: '12px',
  fontWeight: 800,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

const recoveryButtonStyles = {
  color: '#FFFFFF',
  borderColor: '#C24444',
  backgroundColor: '#C24444',
  fontSize: '9px',
  lineHeight: 1.2,
  textTransform: 'uppercase',
  fontWeight: 700,
  padding: '4px 6px',
  minWidth: 'auto',
  '&:hover': {
    borderColor: '#a03737',
    backgroundColor: '#a03737',
  },
};

const creditNamesToCsv = (items?: { name: string }[]): string =>
  (items || []).map((i) => i.name).join(', ');
const csvToCreditNames = (csv: string): { name: string }[] =>
  csv
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .map((name) => ({ name }));

interface MetadataEditModalProps {
  repertoireData: UserRepertoireData;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}

// Edit the user-owned repertoire's display metadata (title, credits, year, goal
// tempo). Deterministic/pipeline-owned fields (level, complexity, preview) are not
// editable. Sends only a non-empty title (the row's name is not-null) and only a
// valid integer year / goal tempo; credit arrays are sent as-is so clearing a field
// clears that relation (creator is optional for a private upload).
const darkFieldSx = {
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiInputLabel-root': { color: '#9AA3B2' },
  '& .MuiFormHelperText-root': { color: '#6B7280' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#767C8C' },
};

const MetadataEditModal = ({ repertoireData, open, onClose, onSaved }: MetadataEditModalProps) => {
  const { currentAuthToken } = useAuthWrapperContext();
  const [title, setTitle] = useState(repertoireData.name);
  const [composers, setComposers] = useState(creditNamesToCsv(repertoireData.composers));
  const [artists, setArtists] = useState(creditNamesToCsv(repertoireData.artists));
  const [arrangers, setArrangers] = useState(creditNamesToCsv(repertoireData.arrangers));
  const [genres, setGenres] = useState(creditNamesToCsv(repertoireData.genres));
  const [year, setYear] = useState(repertoireData.year != null ? String(repertoireData.year) : '');
  const [goalBpm, setGoalBpm] = useState(
    repertoireData.bpm != null ? String(repertoireData.bpm) : '',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setTitle(repertoireData.name);
    setComposers(creditNamesToCsv(repertoireData.composers));
    setArtists(creditNamesToCsv(repertoireData.artists));
    setArrangers(creditNamesToCsv(repertoireData.arrangers));
    setGenres(creditNamesToCsv(repertoireData.genres));
    setYear(repertoireData.year != null ? String(repertoireData.year) : '');
    setGoalBpm(repertoireData.bpm != null ? String(repertoireData.bpm) : '');
    setError('');
  }, [open, repertoireData]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const token = await currentAuthToken();
      const body: Record<string, unknown> = {
        composers: csvToCreditNames(composers),
        artists: csvToCreditNames(artists),
        arrangers: csvToCreditNames(arrangers),
        genres: csvToCreditNames(genres),
      };
      if (title.trim()) body.name = title.trim();
      const parsedYear = parseInt(year, 10);
      if (year.trim() && Number.isFinite(parsedYear)) body.year = parsedYear;
      const parsedBpm = parseInt(goalBpm, 10);
      if (goalBpm.trim() && Number.isFinite(parsedBpm)) body.bpm = parsedBpm;

      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire/${repertoireData.id}/metadata`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSaved();
      onClose();
    } catch (e: unknown) {
      const message =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string | string[] } } }).response?.data?.message
          : undefined;
      setError(Array.isArray(message) ? message.join(', ') : message || 'Could not save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: '#07111D', color: 'white' } }}
    >
      <DialogTitle sx={{ color: 'white' }}>
        Edit details
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: '#9AA3B2' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        <TextField
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          sx={darkFieldSx}
        />
        <TextField
          label="Composer(s)"
          helperText="Comma-separated. Leave blank if unknown."
          value={composers}
          onChange={(e) => setComposers(e.target.value)}
          fullWidth
          sx={darkFieldSx}
        />
        <TextField
          label="Artist(s)"
          helperText="Comma-separated."
          value={artists}
          onChange={(e) => setArtists(e.target.value)}
          fullWidth
          sx={darkFieldSx}
        />
        <TextField
          label="Arranger(s)"
          helperText="Comma-separated."
          value={arrangers}
          onChange={(e) => setArrangers(e.target.value)}
          fullWidth
          sx={darkFieldSx}
        />
        <TextField
          label="Genre(s)"
          helperText="Comma-separated."
          value={genres}
          onChange={(e) => setGenres(e.target.value)}
          fullWidth
          sx={darkFieldSx}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Year"
            type="number"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            sx={darkFieldSx}
          />
          <TextField
            label="Goal tempo (BPM)"
            helperText="The piece's full speed"
            type="number"
            value={goalBpm}
            onChange={(e) => setGoalBpm(e.target.value)}
            sx={darkFieldSx}
          />
        </Box>
        {error && <Box sx={{ color: '#C24444', fontSize: '0.85rem' }}>{error}</Box>}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'flex-end', gap: '12px', padding: '16px' }}>
        <Button onClick={onClose} disabled={saving} sx={{ color: '#9AA3B2' }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          sx={{ backgroundColor: '#469F6A', '&:hover': { backgroundColor: '#3a8659' } }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface UserRepertoireItemProps {
  repertoireData: UserRepertoireData;
  onDelete: () => void;
}

const UserRepertoireItem = ({ repertoireData, onDelete }: UserRepertoireItemProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentAuthToken } = useAuthWrapperContext();
  const repertoireReducerData = useSelector((state: RepertoireReducer) => state.repertoireReducer);
  const isProcessing = isActiveProcessingStatus(repertoireData.status);
  const isError = repertoireData.status === 'failed' || repertoireData.status === 'error';
  const isIncomplete = !isProcessing && !isError && !isUserRepertoirePlayable(repertoireData);
  const [showReuploadModal, setShowReuploadModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');

  const getArtistOrComposerName = () => {
    if (repertoireData.source === CREATED_REPERTOIRE_SOURCE) {
      return CREATED_REPERTOIRE_AUTHOR;
    }
    if (repertoireData.artists && repertoireData.artists.length > 0) {
      return repertoireData.artists.map((a) => a.name).join(', ');
    }
    if (repertoireData.composers && repertoireData.composers.length > 0) {
      return repertoireData.composers.map((c) => c.name).join(', ');
    }
    return 'Unknown';
  };

  const handleClick = () => {
    if (isUserRepertoirePlayable(repertoireData)) {
      dispatch(
        repertoireActions.fetchRepertoireDataSuccess(
          upsertById(
            repertoireReducerData.repertoireData ?? [],
            repertoireData as unknown as RepertoireData,
          ),
        ),
      );
      dispatch(repertoireActions.setRepertoireSelect({ repertoireIdSelect: repertoireData.id }));
      setTimeout(() => {
        navigate('/repertoire-play');
      }, 150);
    }
  };

  const handleReupload = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowReuploadModal(true);
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteModal(true);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowEditModal(true);
  };

  const handleRetry = async (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsRetrying(true);
    setRetryError('');
    try {
      const token = await currentAuthToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire/${repertoireData.id}/reprocess`,
        undefined,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      await onDelete();
    } catch (error: unknown) {
      const message =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setRetryError(message || 'Could not retry processing');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const token = await currentAuthToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire/${repertoireData.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      setShowDeleteModal(false);
      onDelete(); // Call the parent's refresh function
    } catch (error) {
      console.error('Failed to delete repertoire:', error);
      // Could add error handling here
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
  };

  return (
    <React.Fragment>
      <Box
        sx={{
          minWidth: '160px',
          maxWidth: '160px',
          height: '180px',
          backgroundColor: '#07111D',
          borderRadius: '6px',
          border: isError ? '2px solid #C24444' : '2px solid #767C8C',
          position: 'relative',
          cursor: isProcessing || isError || isIncomplete ? 'default' : 'pointer',
          opacity: isProcessing || isError || isIncomplete ? 0.7 : 1,
        }}
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Edit Icon - appears on hover */}
        {isHovered && !isProcessing && (
          <IconButton
            onClick={handleEditClick}
            sx={{
              position: 'absolute',
              top: 8,
              right: 44,
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#9AA3B2',
              width: '32px',
              height: '32px',
              '&:hover': {
                backgroundColor: 'rgba(70, 159, 106, 0.2)',
                color: '#469F6A',
              },
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        )}
        {/* Delete Icon - appears on hover */}
        {isHovered && !isProcessing && (
          <IconButton
            onClick={handleDeleteClick}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              zIndex: 2,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              color: '#C24444',
              width: '32px',
              height: '32px',
              '&:hover': {
                backgroundColor: 'rgba(196, 68, 68, 0.2)',
                color: '#ff6b6b',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        )}
        <Box
          sx={{
            width: '100%',
            height: '100px',
            backgroundColor: 'white',
            position: 'relative',
            borderTopLeftRadius: '4px',
            borderTopRightRadius: '4px',
            borderBottom: '2px solid #767C8C',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {isProcessing ? (
            <CircularProgress size={40} sx={{ color: '#469F6A' }} />
          ) : isError || isIncomplete ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                gap: '8px',
              }}
            >
              <Box sx={{ color: '#C24444', fontSize: '12px', textAlign: 'center' }}>
                {isError ? 'Processing Failed' : 'Setup Incomplete'}
              </Box>
              <Box sx={{ display: 'flex', gap: '6px' }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRetry}
                  disabled={isRetrying}
                  sx={recoveryButtonStyles}
                >
                  {isRetrying ? 'Retrying' : 'Retry'}
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReupload}
                  disabled={isRetrying}
                  sx={recoveryButtonStyles}
                >
                  Replace
                </Button>
              </Box>
              {(retryError ||
                (process.env.NODE_ENV === 'development' && repertoireData.processingError)) && (
                <Box
                  title={
                    retryError ||
                    (process.env.NODE_ENV === 'development'
                      ? repertoireData.processingError || undefined
                      : undefined)
                  }
                  sx={{
                    color: '#C24444',
                    fontSize: '9px',
                    maxWidth: '145px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {retryError || repertoireData.processingError}
                </Box>
              )}
            </Box>
          ) : (
            repertoireData.repertoire_png_preview_url && (
              <PresignedImg
                repertoireId={repertoireData.id}
                style={{
                  width: '94%',
                  height: 'auto',
                  alignSelf: 'flex-start',
                  display: 'block',
                }}
                src={repertoireData.repertoire_png_preview_url}
                alt={repertoireData.name}
              />
            )
          )}
        </Box>
        <Box
          sx={{
            height: '74px',
            paddingBottom: '6px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingLeft: '8px',
            paddingRight: '6px',
          }}
        >
          <Box
            sx={{
              marginTop: '5px',
              marginBottom: '7px',
              color: '#FFFFFF',
              fontFamily: 'Lato',
              fontSize: '14px',
              fontWeight: '400',
              lineHeight: '17px',
              letterSpacing: '0.01em',
              textAlign: 'left',
            }}
          >
            {repertoireData.name}
          </Box>
          <Box
            sx={{
              marginBottom: '2px',
              color: '#FFFFFF',
              fontFamily: 'Lato',
              fontSize: '14px',
              fontStyle: 'italic',
              fontWeight: '700',
              lineHeight: '17px',
              letterSpacing: '0.01em',
              textAlign: 'left',
              textOverflow: 'ellipsis',
              maxWidth: '140px',
              maxHeight: '30px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
            }}
          >
            {getArtistOrComposerName()}
          </Box>
        </Box>
      </Box>
      {showEditModal && (
        <MetadataEditModal
          repertoireData={repertoireData}
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          onSaved={onDelete}
        />
      )}
      {showReuploadModal && (
        <UploadModal
          open={showReuploadModal}
          onClose={() => setShowReuploadModal(false)}
          repertoireId={repertoireData.id}
          defaultComposerName={repertoireData.composers?.[0]?.name || ''}
          onSuccess={() => {
            setShowReuploadModal(false);
            onDelete();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteModal}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: '#050B13',
            border: '2px solid #C24444',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#FFFFFF',
            fontFamily: 'Lato',
            fontSize: '18px',
            fontWeight: '700',
            textAlign: 'center',
          }}
        >
          Delete Repertoire
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#FFFFFF', textAlign: 'center', padding: '16px 0' }}>
            Are you sure you want to delete "{repertoireData.name}"? This action cannot be undone.
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: '16px', padding: '16px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            disabled={isDeleting}
            sx={{
              color: '#FFFFFF',
              borderColor: '#767C8C',
              textTransform: 'uppercase',
              fontWeight: '600',
              '&:hover': {
                borderColor: '#469F6A',
                backgroundColor: 'rgba(70, 159, 106, 0.1)',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isDeleting}
            sx={{
              backgroundColor: '#C24444',
              color: 'white',
              textTransform: 'uppercase',
              fontWeight: '600',
              '&:hover': {
                backgroundColor: '#a03737',
              },
              '&:disabled': {
                backgroundColor: '#767C8C',
                color: '#ADB1B7',
              },
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  repertoireId?: number;
  defaultComposerName?: string;
}

const UploadModal = ({
  open,
  onClose,
  onSuccess,
  repertoireId,
  defaultComposerName = '',
}: UploadModalProps) => {
  const { currentAuthToken, user } = useAuthWrapperContext();
  const [formData, setFormData] = useState({
    file: null as File | null,
  });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [composerName, setComposerName] = useState(defaultComposerName);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) setComposerName(defaultComposerName);
  }, [defaultComposerName, open]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate MusicXML file
      const lowerCaseName = file.name.toLowerCase();
      if (
        !lowerCaseName.endsWith('.xml') &&
        !lowerCaseName.endsWith('.musicxml') &&
        !lowerCaseName.endsWith('.mxml') &&
        !lowerCaseName.endsWith('.mxl')
      ) {
        setError('Please upload a valid MusicXML file (.xml, .musicxml, .mxml, or .mxl)');
        return;
      }
      if (file.size > MAX_UPLOAD_BYTES) {
        setError('MusicXML files must be 20 MB or smaller');
        return;
      }
      setFormData((prev) => ({ ...prev, file }));
      setError('');

      // .mxl files are ZIP containers; leave the field editable when the
      // browser cannot directly read MusicXML metadata from the selected file.
      if (!file.name.toLowerCase().endsWith('.mxl')) {
        try {
          const extractedComposer = extractComposerFromMusicXml(await file.text());
          if (extractedComposer) setComposerName(extractedComposer);
        } catch {
          // Metadata is a convenience. A readable file can still be uploaded
          // after the user supplies the composer manually.
        }
      }
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.file) {
      setError('Please select a file to upload');
      return;
    }
    setUploading(true);
    setError('');

    try {
      const token = await currentAuthToken();
      const uploadFormData = new FormData();

      uploadFormData.append('musicXml', formData.file);
      // Creator is optional for a private upload; only send one when typed.
      if (composerName.trim()) {
        uploadFormData.append('composers', JSON.stringify([{ name: composerName.trim() }]));
      }
      if (!repertoireId && user?.sub) {
        uploadFormData.append(
          'idempotencyKey',
          await deriveUploadIdempotencyKey(formData.file, user.sub),
        );
      }

      const endpoint = `${process.env.REACT_APP_BACKEND_URL}/api/v1/repertoires/user-repertoire${
        repertoireId ? `/${repertoireId}` : ''
      }`;
      await axios.request({
        method: repertoireId ? 'patch' : 'post',
        url: endpoint,
        data: uploadFormData,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Reset form
      setFormData({
        file: null,
      });
      setComposerName(defaultComposerName);
      setError('');

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      onSuccess();
      onClose();
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to upload repertoire');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog
      data-repertoire-upload-dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#050B13',
          border: '2px solid #469F6A',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: '#FFFFFF',
          fontFamily: 'Lato',
          fontSize: '18px',
          fontWeight: '700',
          textAlign: 'center',
          position: 'relative',
        }}
      >
        {repertoireId ? 'Replace Repertoire File' : 'Upload Repertoire'}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: '#FFFFFF',
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', paddingTop: '16px' }}>
          <Box>
            <input
              type="file"
              accept=".xml,.mxl,.musicxml,.mxml"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{
                  color: '#FFFFFF',
                  borderColor: '#767C8C',
                  textTransform: 'none',
                  '&:hover': {
                    borderColor: '#469F6A',
                    backgroundColor: 'rgba(70, 159, 106, 0.1)',
                  },
                }}
              >
                {formData.file ? formData.file.name : 'Choose MusicXML File'}
              </Button>
            </label>
          </Box>
          <TextField
            value={composerName}
            onChange={(event) => setComposerName(event.target.value)}
            label="Composer"
            disabled={uploading}
            fullWidth
            inputProps={{
              maxLength: 200,
              'data-repertoire-upload-composer': true,
            }}
            InputLabelProps={{ sx: { color: '#ADB1B7' } }}
            InputProps={{ sx: { color: '#FFFFFF' } }}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': { borderColor: '#767C8C' },
                '&:hover fieldset': { borderColor: '#469F6A' },
                '&.Mui-focused fieldset': { borderColor: '#469F6A' },
              },
            }}
          />
          {error && (
            <Box sx={{ color: '#C24444', fontSize: '14px', textAlign: 'center' }}>{error}</Box>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={uploading}
            sx={{
              backgroundColor: '#469F6A',
              color: 'white',
              textTransform: 'uppercase',
              fontWeight: '700',
              fontSize: '15px',
              padding: '12px 32px',
              '&:hover': {
                backgroundColor: '#3a8556',
              },
              '&:disabled': {
                backgroundColor: '#767C8C',
                color: '#ADB1B7',
              },
            }}
          >
            {uploading ? 'Uploading...' : repertoireId ? 'Replace File' : 'Upload'}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
